import { ref } from 'vue';
import api from '@/services/api';

interface Product {
  id: string;
  name: string;
  slug: string;
  price: string;
  mainPicture: string | null;
  followersCount: number;
  city: string;
  username: string;
  category?: {
    id: string;
    name: string;
  };
}

interface ProductsMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface ProductsResponse {
  status: string;
  message: string;
  data: {
    data: Product[];
    meta: ProductsMeta;
  };
}

interface Toast {
  show: boolean;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
}

export function useFollowedProducts() {
  const products = ref<Product[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const meta = ref<ProductsMeta>({
    total: 0,
    page: 1,
    limit: 12,
    totalPages: 1,
  });
  const toast = ref<Toast>({
    show: false,
    message: '',
    type: 'info',
  });

  /**
   * Show a toast notification
   */
  const showToast = (
    message: string,
    type: 'info' | 'success' | 'warning' | 'error' = 'info'
  ) => {
    toast.value = {
      show: true,
      message,
      type,
    };

    // Automatically hide toast after 3 seconds
    setTimeout(() => {
      toast.value.show = false;
    }, 3000);
  };

  /**
   * Fetch followed products with pagination
   */
  const fetch = async (page: number = 1) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.get<ProductsResponse>(
        `/api/products/followed/list?page=${page}`
      );

      if (response.data.status === 'success') {
        products.value = response.data.data.data;
        meta.value = response.data.data.meta;
      } else {
        error.value = 'Greška prilikom učitavanja praćenih proizvoda.';
      }
    } catch (err) {
      console.error('Error fetching followed products:', err);
      error.value = 'Greška prilikom komunikacije sa serverom.';
    } finally {
      loading.value = false;
    }
  };

  /**
   * Unfollow a product with smart pagination handling
   */
  const unfollowWithPagination = async (
    productId: string,
    currentPage: number,
    onPageChange: (page: number) => void
  ) => {
    try {
      // Check if this is the last item on current page and we're not on page 1
      const isLastItemOnPage = products.value.length === 1;
      const isOnPageGreaterThan1 = currentPage > 1;

      await api.delete(`/api/product-follows/products/${productId}/unfollow`);

      // Remove product from list
      products.value = products.value.filter(
        (product) => product.id !== productId
      );

      // Update meta data
      if (meta.value.total > 0) {
        meta.value.total -= 1;
        meta.value.totalPages = Math.ceil(meta.value.total / meta.value.limit);
      }

      // Handle pagination logic
      if (isLastItemOnPage && isOnPageGreaterThan1) {
        // Go to previous page and fetch data
        const newPage = currentPage - 1;
        onPageChange(newPage);
        await fetch(newPage);
      }

      showToast('Proizvod je uspešno otpraćen', 'success');
    } catch (err) {
      console.error('Error unfollowing product:', err);
      showToast('Greška prilikom otpraćivanja proizvoda', 'error');
    }
  };

  /**
   * Regular unfollow without pagination handling (for backward compatibility)
   */
  const unfollow = async (productId: string) => {
    try {
      await api.delete(`/api/product-follows/products/${productId}/unfollow`);

      // Remove product from list
      products.value = products.value.filter(
        (product) => product.id !== productId
      );

      // Update meta data
      if (meta.value.total > 0) {
        meta.value.total -= 1;
        meta.value.totalPages = Math.ceil(meta.value.total / meta.value.limit);
      }

      showToast('Proizvod je uspešno otpraćen', 'success');
    } catch (err) {
      console.error('Error unfollowing product:', err);
      showToast('Greška prilikom otpraćivanja proizvoda', 'error');
    }
  };

  return {
    products,
    loading,
    error,
    meta,
    toast,
    fetch,
    unfollow,
    unfollowWithPagination,
    showToast,
  };
}
